// Listen mode: Breeze listening in on a call held somewhere else (Teams,
// Zoom, Meet), for the operator's own use. It is an ordinary one-off room with
// two seats and no video: the operator's microphone speaks for the operator,
// and the other side of the call (shared system or tab audio) speaks for the
// guest. The Room runs the same assistant it runs in a Breeze call.
//
// These are the rules both ends share. The Worker decides who may mark a room
// as a listen room; the web client builds and reads the page's address.

/** The query parameter the web client puts on a room's WebSocket to ask for
 * listen mode. The Worker strips it from every join and sets it again only
 * for the proven operator (`listenRoomAllowed`). */
export const LISTEN_PARAM = "listen";

export interface ListenGate {
  /** Which kind of room this join is for. */
  readonly kind: string;
  /** The account the join's user key proves, or null for an unkeyed join. */
  readonly userId: string | null;
  /** That account's handle, lowercase, or null. */
  readonly handle: string | null;
  /** The account that made the one-off room, or null when nobody did. */
  readonly ownerId: string | null;
  /** The deployment's operator handle (`OPERATOR_HANDLE`), or null when unset. */
  readonly operatorHandle: string | null;
  /** The join asked for listen mode. */
  readonly asked: boolean;
}

/** A room is a listen room only when the operator, signed in with their own
 * key, asked for it on a one-off room they made. A guest seat can never set
 * it, so a stranger cannot reach the agenda lookup or switch off auto record
 * on someone else's room. */
export function listenRoomAllowed(gate: ListenGate): boolean {
  return (
    gate.asked &&
    gate.kind === "once" &&
    !!gate.userId &&
    !!gate.ownerId &&
    gate.userId === gate.ownerId &&
    !!gate.operatorHandle &&
    gate.handle === gate.operatorHandle.toLowerCase()
  );
}

export interface AgendaGate {
  /** The host who turned the assistant on is the room's proven owner. */
  readonly ownerStarted: boolean;
  readonly slug: string | null;
  readonly operatorHandle: string | null;
  /** The room was marked a listen room by the operator. */
  readonly listen: boolean;
}

/** Whether Breeze may look for this call's agenda: only for the operator,
 * started by the room's proven owner, in the operator's personal room or in a
 * listen room. */
export function agendaSearchAllowed(gate: AgendaGate): boolean {
  if (!gate.ownerStarted || !gate.operatorHandle) return false;
  if (gate.listen) return true;
  return !!gate.slug && gate.slug === gate.operatorHandle;
}

/** What the Listen page needs to know about the call it is listening to. */
export interface ListenTarget {
  /** The other person's name, as booked. Labels their side of the transcript. */
  readonly guest: string;
  /** The call's prep page slug, or empty. */
  readonly prepSlug: string;
  /** The app the call runs in ("Teams", "Zoom", "Meet"), or empty. */
  readonly app: string;
  /** Whether the link says the say-next prompts belong on this call. A link
   * from the agendas sets "on" for a call that is not with a sales prospect;
   * empty leaves it to the page. */
  readonly say: "on" | "off" | "";
}

/** The address of the Listen page for a call. */
export function listenPath(target: Partial<ListenTarget>): string {
  const parts: string[] = [];
  const add = (key: string, value: string | undefined, max: number): void => {
    const text = value?.trim();
    if (text) parts.push(`${key}=${encodeURIComponent(text.slice(0, max))}`);
  };
  add("guest", target.guest, 60);
  add("prep", target.prepSlug, 80);
  add("app", target.app, 20);
  if (target.say === "on" || target.say === "off") add("say", target.say, 3);
  return parts.length > 0 ? `/listen?${parts.join("&")}` : "/listen";
}

function queryValue(search: string, key: string): string {
  const query = search.includes("?") ? search.slice(search.indexOf("?") + 1) : search;
  for (const pair of query.split("&")) {
    const at = pair.indexOf("=");
    const name = at < 0 ? pair : pair.slice(0, at);
    if (name !== key) continue;
    try {
      return decodeURIComponent((at < 0 ? "" : pair.slice(at + 1)).replace(/\+/g, " "));
    } catch {
      return "";
    }
  }
  return "";
}

/** Reads a Listen page's address back into its target. */
export function parseListenQuery(search: string): ListenTarget {
  return {
    guest: queryValue(search, "guest").trim().slice(0, 60),
    prepSlug: queryValue(search, "prep").trim().slice(0, 80),
    app: queryValue(search, "app").trim().slice(0, 20),
    say: ((v) => (v === "on" || v === "off" ? v : ""))(queryValue(search, "say").trim().toLowerCase()),
  };
}

/** The seat name for the other side. A booking with no name is still a person
 * on the call, so it gets a plain word instead of an empty label. */
export function listenGuestName(target: Pick<ListenTarget, "guest">): string {
  return target.guest.trim() || "Guest";
}

// The listening indicator (Adam, 2026-10-05: "a minimal listening indicator,
// and i'll have the prep page pulled up while on the teams call so i want an
// indicator and stop button on that page as well"). The Listen tab reports in
// every few seconds; any page of his, on any origin (through its own server),
// reads whether Breeze is listening and can ask it to stop. The report keeps
// no words and no sound: only whether each side is coming in.

/** How often the Listen tab reports in. */
export const LISTEN_BEAT_MS = 3000;
/** A report older than this means nobody is listening any more (a closed tab
 * or a lost network), so the indicator goes out on its own. */
export const LISTEN_STALE_MS = 12_000;

export type ListenSideWord = "idle" | "asking" | "on" | "lost" | "off";

/** How a stopped session's recording ended up: "saved" when every piece
 * reached the Worker, "partial" when a piece was lost or the last one was
 * still uploading when the wait ran out, "none" when nothing was recorded. */
export type ListenSaved = "saved" | "partial" | "none";

/** How long after a session ends `GET /api/listen/status` still says how it
 * ended, so a page that showed it listening can show "Recording saved". */
export const LISTEN_ENDED_VISIBLE_MS = 30_000;

/** One line of a Listen session's transcript as the operator's pages show
 * it (QUEUE row 324). Adam, 2026-10-08: "i want this to stream the
 * transcription as it comes in; i also want the one rendered on the prep page
 * to do the same, but make that hideable on the prep page". */
export interface ListenLine {
  /** Epoch ms the line was heard. */
  readonly at: number;
  /** Who said it: the operator's name or the guest's. */
  readonly name: string;
  readonly text: string;
  /** Still being said: the words so far of the sentence in progress, which
   * the next update replaces. Never part of the saved transcript. */
  readonly live?: boolean;
}

/** The most lines a beat carries and `GET /api/listen/status` returns. */
export const LISTEN_LINES_MAX = 60;
/** And the most characters across them, so a beat stays small. */
export const LISTEN_LINES_CHARS = 12_000;

/** The newest lines that fit both caps, oldest first. Anything that is not a
 * line is dropped, so a beat body can be passed straight in. */
export function capListenLines(lines: unknown): ListenLine[] {
  if (!Array.isArray(lines)) return [];
  const out: ListenLine[] = [];
  let chars = 0;
  for (let i = lines.length - 1; i >= 0 && out.length < LISTEN_LINES_MAX; i--) {
    const l = lines[i] as Partial<ListenLine> | null;
    if (!l || typeof l.text !== "string" || typeof l.name !== "string" || typeof l.at !== "number" || !Number.isFinite(l.at)) continue;
    const text = l.text.trim().slice(0, 2000);
    if (!text) continue;
    chars += text.length;
    if (chars > LISTEN_LINES_CHARS) break;
    out.push({ at: l.at, name: l.name.trim().slice(0, 60), text, ...(l.live === true ? { live: true } : {}) });
  }
  return out.reverse();
}

/** `POST /api/listen/beat`, from the Listen tab with the operator's key. */
export interface ListenBeat {
  /** The session's one-off room code, so a new session is told from an old one. */
  readonly code: string;
  readonly guest: string;
  readonly mine: ListenSideWord;
  readonly theirs: ListenSideWord;
  /** The Listen tab is paused: nothing is heard or recorded. */
  readonly paused?: boolean;
  /** The tab's own last events (a share lost, a seat reconnecting, the tab hidden), newest last. */
  readonly trail?: readonly string[];
  /** Stop was pressed and the last piece of the recording is uploading. */
  readonly saving?: boolean;
  /** The newest transcript lines, oldest first (capListenLines). */
  readonly lines?: readonly ListenLine[];
}

/** `POST /api/listen/end`: why the Listen tab ended the session. */
export interface ListenEnd {
  readonly code: string;
  readonly reason?: string;
  readonly trail?: readonly string[];
  readonly saved?: ListenSaved;
}

/** One finished Listen session, as `GET /api/listen/log` lists it, newest first.
 * `endedBy` is the tab's own reason, or `no_end_report` when the tab vanished
 * without saying (closed, crashed, or a network gone for good). */
export interface ListenLogEntry {
  readonly code: string;
  readonly guest: string;
  readonly since: number;
  /** The last report the tab made, or the end report's time. */
  readonly last: number;
  readonly endedBy: string;
  readonly trail: readonly string[];
}

/** The answer to a beat: `stop` is true once any page has asked to stop, and
 * `pause` carries a pause or resume another page asked for (absent when none
 * is waiting). */
export interface ListenBeatReply {
  readonly stop: boolean;
  readonly pause?: boolean;
}

/** `GET /api/listen/status`. */
export interface ListenStatus {
  readonly listening: boolean;
  readonly guest?: string;
  readonly mine?: ListenSideWord;
  readonly theirs?: ListenSideWord;
  /** The Listen tab is paused. */
  readonly paused?: boolean;
  /** Epoch ms the session began. */
  readonly since?: number;
  /** Stop was asked for and the Listen tab has not yet let go. */
  readonly stopping?: boolean;
  /** The Listen tab is uploading the last piece of the recording. */
  readonly saving?: boolean;
  /** The newest transcript lines, oldest first, as the Listen tab last
   * reported them. */
  readonly lines?: readonly ListenLine[];
  /** With `listening: false`: the last session ended `ago` ms back (within
   * LISTEN_ENDED_VISIBLE_MS), and how its recording ended up. */
  readonly ended?: { readonly code: string; readonly saved: ListenSaved; readonly ago: number };
}
